// User types
export type UserDepartment = 'vertrieb' | 'buchhaltung' | 'technik' | 'geschaeftsfuehrung' | 'sonstige';

export const userDepartmentLabels: Record<UserDepartment, string> = {
  vertrieb: 'Vertrieb',
  buchhaltung: 'Buchhaltung',
  technik: 'Technik',
  geschaeftsfuehrung: 'Geschäftsführung',
  sonstige: 'Sonstige',
};

export interface User {
  id: string;
  email: string;
  name: string;
  role: 'admin' | 'user' | 'viewer';
  // Die Taetigkeit (ADR-0009). Sie traegt die Rechte, `role` faellt.
  // Optional, weil `usersApi.list()` denselben Typ benutzt und die
  // Users-API das Feld (noch) nicht ausliefert — nur /session tut es.
  profile?: string | null;
  avatar_url: string | null;
  department: UserDepartment | null;
  picture: string | null;
}

// Customer types
export type CustomerStatus =
  | 'lead_open'
  | 'lead_contacted'
  | 'lead_ask_on_site'
  | 'lead_lost'
  | 'viewing_scheduled'
  | 'quote_to_send'
  | 'ask_on_site'
  | 'quote_sent'
  | 'won'
  | 'lost'
  | 'interested_later';

export const LEAD_STATUSES: readonly CustomerStatus[] = [
  'lead_open',
  'lead_contacted',
  'lead_ask_on_site',
  'lead_lost',
] as const;

export const KUNDE_STATUSES: readonly CustomerStatus[] = [
  'viewing_scheduled',
  'quote_to_send',
  'ask_on_site',
  'quote_sent',
  'won',
  'lost',
  'interested_later',
] as const;

// Statustexte und -töne kommen aus STATUS von @elbklar/ui (statusInfo), in jeder
// App gleich (Auftrag 009, E3). Ein neuer Status wird zuerst dort eingetragen.

export type CustomerType = 'company' | 'person';
// Spiegel von elbklar_core.models.customer.CustomerBusinessType — die eine
// Quelle. Bis zum 22.10.2026 kannte diese Liste 'privat', 'oeffentlich' und
// 'facility_manager', core aber nicht (Audit C9). Altwerte in der Datenbank
// liefert die API weiterhin als rohen String; die Label-Funktion unten faellt
// dann auf den Wert selbst zurueck.
export type CustomerBusinessType =
  | 'hausverwaltung'
  | 'eigentuemer'
  | 'bautraeger'
  | 'weg'
  | 'genossenschaft'
  | 'kommunal'
  | 'sonstige';
export type CustomerIndustry = 'privat_immobilien' | 'industrie' | 'oeffentlich' | 'gewerbe' | 'wohnungswirtschaft';
export type AcquisitionSource = 'website' | 'empfehlung' | 'messe' | 'kaltakquise' | 'partner' | 'bestandskunde' | 'social_media' | 'sonstige';

export interface Customer {
  id: string;
  customer_number: string;
  company_name: string;
  display_name: string | null;
  type: CustomerType;
  status: CustomerStatus;
  email: string | null;
  phone: string | null;
  website: string | null;
  vat_number: string | null;
  street: string | null;
  postal_code: string | null;
  city: string | null;
  // Facade cleaning industry fields
  customer_business_type: CustomerBusinessType | null;
  industry: CustomerIndustry | null;
  managed_objects_count: number;
  acquisition_source: AcquisitionSource | null;
  assigned_to: string | null;
  assigned_user_name: string | null;
  primary_contact_id: string | null;
  primary_contact_name: string | null;
  notes: string | null;
  tags: string[];
  lost_reason?: string | null;
  follow_up_at?: string | null;
  last_interaction_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface CustomerCreate {
  company_name: string;
  display_name?: string;
  type: CustomerType;
  status: CustomerStatus;
  email?: string;
  phone?: string;
  // null statt undefined ist hier bedeutungstragend: Der PATCH ignoriert
  // ungesetzte Felder, ein Feld liesse sich sonst nie wieder leeren.
  website?: string | null;
  vat_number?: string | null;
  street?: string | null;
  postal_code?: string | null;
  city?: string | null;
  // Facade cleaning industry fields
  customer_business_type?: CustomerBusinessType;
  industry?: CustomerIndustry;
  managed_objects_count?: number | null;
  acquisition_source?: AcquisitionSource;
  assigned_to?: string;
  notes?: string;
  tags?: string[];
  lost_reason?: string | null;
  follow_up_at?: string | null;
  contacts?: Array<{
    first_name: string;
    last_name: string;
    email?: string;
    phone?: string;
    mobile?: string;
    role?: string;
    is_primary?: boolean;
  }>;
}

// Customer label mappings for UI
export const customerBusinessTypeLabels: Record<CustomerBusinessType, string> = {
  hausverwaltung: 'Hausverwaltung',
  eigentuemer: 'Eigentümer',
  bautraeger: 'Bauträger',
  weg: 'Wohnungseigentümergemeinschaft',
  genossenschaft: 'Genossenschaft',
  kommunal: 'Kommunal',
  sonstige: 'Sonstige',
};

// Eine Optionsliste fuer alle Formulare (Kunde anlegen/bearbeiten, Lead
// umwandeln), damit nicht jedes Formular seine eigene Werteliste fuehrt.
export const customerBusinessTypeOptions: Array<{ value: CustomerBusinessType; label: string }> = (
  Object.keys(customerBusinessTypeLabels) as CustomerBusinessType[]
).map((value) => ({ value, label: customerBusinessTypeLabels[value] }));

// Label fuer einen Wert aus der API; ein Altwert, den core nicht kennt, wird
// so angezeigt, wie er in der Datenbank steht, statt leer zu bleiben.
export function customerBusinessTypeLabel(value: string | null | undefined): string {
  if (!value) return '';
  return (customerBusinessTypeLabels as Record<string, string>)[value] ?? value;
}

export const customerTypeLabels: Record<CustomerType, string> = {
  company: 'Firma',
  person: 'Privatperson',
};

export const customerIndustryLabels: Record<CustomerIndustry, string> = {
  privat_immobilien: 'Privat Immobilien',
  industrie: 'Industrie',
  oeffentlich: 'Öffentlich',
  gewerbe: 'Gewerbe',
  wohnungswirtschaft: 'Wohnungswirtschaft',
};

export const acquisitionSourceLabels: Record<AcquisitionSource, string> = {
  website: 'Website',
  empfehlung: 'Empfehlung',
  messe: 'Messe',
  kaltakquise: 'Kaltakquise',
  partner: 'Partner',
  bestandskunde: 'Bestandskunde',
  social_media: 'Social Media',
  sonstige: 'Sonstige',
};

// Contact types
export interface Contact {
  id: string;
  customer_id: string | null;
  partner_id: string | null;
  customer_name?: string | null;
  customer_number?: string | null;
  partner_name?: string | null;
  partner_number?: string | null;
  first_name: string;
  last_name: string;
  email: string | null;
  phone: string | null;
  mobile: string | null;
  role: string | null;
  department: string | null;
  is_primary: boolean;
  is_active: boolean;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface ContactCreate {
  customer_id?: string;
  partner_id?: string;
  first_name: string;
  last_name: string;
  email?: string;
  phone?: string;
  mobile?: string;
  role?: string;
  department?: string;
  is_primary?: boolean;
  is_active?: boolean;
  notes?: string;
}

// Interaction types
export type InteractionType = 'call' | 'email' | 'meeting' | 'note' | 'task' | 'quote' | 'invoice' | 'site_visit' | 'other';

export interface Interaction {
  id: string;
  customer_id: string | null;
  partner_id: string | null;
  contact_id: string | null;
  user_id: string | null;
  project_id: string | null;
  type: InteractionType;
  subject: string;
  description: string | null;
  occurred_at: string;
  duration_minutes: number | null;
  outcome: string | null;
  follow_up_date: string | null;
  metadata: Record<string, unknown> | null;
  customer_name?: string | null;
  partner_name?: string | null;
  contact_name?: string | null;
  created_at: string;
  updated_at: string | null;
  /** Aufgabe „Nachfassen: …“, die das Backend zu follow_up_date angelegt hat. */
  wiedervorlage_aufgabe_id?: string | null;
  /** Nur in der Detailantwort: Stand und Fälligkeit dieser Aufgabe. */
  wiedervorlage_aufgabe_status?: 'open' | 'in_progress' | 'completed' | 'cancelled' | 'blocked' | null;
  wiedervorlage_aufgabe_faellig?: string | null;
}

export interface InteractionCreate {
  customer_id?: string;
  partner_id?: string;
  contact_id?: string;
  project_id?: string;
  type: InteractionType;
  subject: string;
  description?: string;
  occurred_at: string;
  duration_minutes?: number;
  outcome?: string;
  /** Wiedervorlage; das Backend legt dazu die Aufgabe an. null entfernt sie beim Bearbeiten. */
  follow_up_date?: string | null;
}

// Pagination types
export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  per_page: number;
  pages: number;
}

// API types
export interface ApiError {
  message: string;
  code: string;
  details?: Record<string, string[]>;
}

// Form types
export interface SelectOption<T = string> {
  value: T;
  label: string;
}

// Partner types
export type PartnerType = 'cleaning_company' | 'netzwerkpartner' | 'subcontractor' | 'real_estate_agent' | 'architect' | 'other';
export type PartnerStatus = 'prospect' | 'active' | 'inactive';

export interface Partner {
  id: string;
  name: string;
  partner_number: string;
  partner_type: PartnerType;
  status: PartnerStatus;
  email: string | null;
  phone: string | null;
  website: string | null;
  street: string | null;
  postal_code: string | null;
  city: string | null;
  country: string;
  assigned_to: string | null;
  assigned_user_name: string | null;
  primary_contact_id: string | null;
  primary_contact_name: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface PartnerCreate {
  name: string;
  partner_type: PartnerType;
  status?: PartnerStatus;
  assigned_to?: string;
  email?: string;
  phone?: string;
  website?: string;
  street?: string;
  postal_code?: string;
  city?: string;
  country?: string;
  notes?: string;
  contacts?: Array<{
    first_name: string;
    last_name: string;
    email?: string;
    phone?: string;
    mobile?: string;
    role?: string;
    is_primary?: boolean;
  }>;
}

// Filter types
export interface CustomerFilters {
  search?: string;
  status?: CustomerStatus;
  page?: number;
}

export interface ContactFilters {
  search?: string;
  customer_id?: string;
  partner_id?: string;
  page?: number;
}

export interface InteractionFilters {
  search?: string;
  customer_id?: string;
  partner_id?: string;
  contact_id?: string;
  type?: InteractionType;
  page?: number;
}

export interface PartnerFilters {
  search?: string;
  partner_type?: PartnerType;
  status?: PartnerStatus;
  page?: number;
}

// Activity Feed
export type ActivityType = 'interaction' | 'email' | 'task';

export interface ActivityFeedItem {
  id: string;
  activity_type: ActivityType;
  timestamp: string;
  icon: string;
  color: string;
  title: string;
  description: string | null;
  customer_name?: string | null;
  partner_name?: string | null;
  contact_name?: string | null;
  interaction_type?: string | null;
  outcome?: string | null;
  email_direction?: string | null;
  email_from?: string | null;
  task_status?: string | null;
  task_priority?: string | null;
  task_due_date?: string | null;
  has_follow_up_task?: boolean;
}

export interface ActivityFeedResponse {
  items: ActivityFeedItem[];
  total: number;
  has_more: boolean;
}

// Verknuepfte Daten aus den gemeinsamen Tabellen (buildings, projects, quotes,
// missions) — GET /customers/{id}/related. Geldwerte sind Decimal-Strings wie
// ueberall in den APIs (Muster finance); vor dem Rechnen Number() anwenden.
export interface RelatedObject {
  id: string;
  name: string;
  address: string;
  object_type: string;
  last_service: string | null;
}

export interface RelatedProject {
  id: string;
  name: string;
  project_number: string;
  status: string;
  start_date: string | null;
  end_date: string | null;
  value: string | null;
}

export interface RelatedQuote {
  id: string;
  quote_number: string;
  title: string;
  status: string;
  total_value: string;
  created_at: string;
  valid_until: string | null;
}

export interface RelatedMission {
  id: string;
  name: string | null;
  object_name: string;
  flight_date: string | null;
  status: string;
  team_size: number;
}

export interface CustomerRelatedData {
  objects: RelatedObject[];
  projects: RelatedProject[];
  quotes: RelatedQuote[];
  missions: RelatedMission[];
  object_count: number;
  project_count: number;
  quote_count: number;
  mission_count: number;
}
